import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, MessageSquare, Zap, CheckCircle2, Clock, Globe,
  Brain, Shield, TrendingUp, Users, Code, Headphones
} from 'lucide-react';

const AIChatbotDevelopment = () => {
  const services = [
    {
      icon: Headphones,
      title: 'Customer Support Chatbots',
      description: 'Resolve 70%+ of tickets automatically with AI that understands your products, policies, and tone',
      features: ['24/7 automated responses', 'Escalation to human agents', 'Multi-language support', 'CRM integration']
    },
    {
      icon: TrendingUp,
      title: 'Sales & Lead Gen Bots',
      description: 'Qualify leads, book demos, and nurture prospects around the clock — no SDR required',
      features: ['Lead qualification flows', 'Calendar integration', 'CRM sync (HubSpot, Salesforce)', 'Personalised follow-up']
    },
    {
      icon: Brain,
      title: 'Knowledge Base Assistants',
      description: 'Internal chatbots trained on your docs, wikis, and SOPs so staff get answers in seconds',
      features: ['RAG over private documents', 'Confluence / Notion / SharePoint', 'Role-based access control', 'Source citations']
    },
    {
      icon: MessageSquare,
      title: 'WhatsApp & Messaging Bots',
      description: 'Deploy AI chatbots on WhatsApp, Slack, Teams, Telegram — wherever your customers already are',
      features: ['WhatsApp Business API', 'Slack & Teams apps', 'Rich media support', 'Broadcast campaigns']
    },
    {
      icon: Code,
      title: 'Voice AI & Phone Bots',
      description: 'AI-powered voice agents that handle inbound calls, bookings, and FAQs with natural conversation',
      features: ['Twilio / ElevenLabs integration', 'IVR replacement', 'Appointment booking', 'Call transcription']
    },
    {
      icon: Shield,
      title: 'Secure Enterprise Chatbots',
      description: 'LLM deployments inside your own cloud — no data leaves your environment',
      features: ['Private Azure OpenAI / AWS Bedrock', 'SOC 2 & HIPAA ready', 'Audit logging', 'SSO integration']
    }
  ];

  const caseStudies = [
    {
      industry: 'Insurance',
      challenge: '500+ daily insurance documents processed manually by a 12-person team',
      solution: 'AI document extraction bot integrated with their CRM — zero manual data entry',
      result: '95% reduction in processing time. Error rate dropped to near zero.',
      timeline: '3 weeks',
      link: '/case-studies/insurance-automation'
    },
    {
      industry: 'Healthcare',
      challenge: 'Patient intake forms and appointment scheduling consuming nurse hours',
      solution: 'WhatsApp AI chatbot handling scheduling, reminders, and pre-visit questionnaires',
      result: '40% fewer missed appointments. 3 hours saved per nurse per day.',
      timeline: '4 weeks',
      link: '/case-studies/healthcare-communication'
    },
    {
      industry: 'Retail & E-commerce',
      challenge: 'Support team overwhelmed with order status, returns, and product queries',
      solution: 'AI chatbot integrated with order management system, answering 80% of queries automatically',
      result: '72% ticket deflection. Support costs cut by 60%.',
      timeline: '3 weeks',
      link: '/case-studies/retail-analytics'
    }
  ];

  const process = [
    { step: '01', title: 'Discovery & Use-Case Mapping', description: 'We identify the highest-ROI chatbot use cases for your business and map the conversation flows', duration: '3–5 days' },
    { step: '02', title: 'Architecture & Integrations', description: 'Design the bot architecture, choose the right LLM, and plan all third-party integrations', duration: '1 week' },
    { step: '03', title: 'Build & Train', description: 'Develop the bot, train on your data, tune prompts, and wire up every integration', duration: '2–5 weeks' },
    { step: '04', title: 'Test, Deploy & Optimise', description: 'Rigorous QA, production deployment, and ongoing performance monitoring', duration: '1 week' }
  ];

  const techStack = [
    { category: 'LLMs', items: ['OpenAI GPT-4o', 'Claude 3.5 Sonnet', 'Gemini Pro', 'Llama 3', 'Mistral'] },
    { category: 'Frameworks', items: ['LangChain', 'LangGraph', 'LlamaIndex', 'Rasa'] },
    { category: 'Channels', items: ['WhatsApp Business API', 'Slack', 'Microsoft Teams', 'Twilio', 'Telegram'] },
    { category: 'Vector DBs', items: ['Pinecone', 'Weaviate', 'pgvector', 'Chroma', 'Qdrant'] },
    { category: 'Voice', items: ['ElevenLabs', 'Deepgram', 'Azure Speech', 'AWS Polly'] },
    { category: 'Integrations', items: ['HubSpot', 'Salesforce', 'Zendesk', 'Intercom', 'Freshdesk'] }
  ];

  return (
    <>
      <Helmet>
        <title>AI Chatbot Development Company | Custom Chatbots Built in Weeks | Agility</title>
        <meta name="description" content="Expert AI chatbot development company building enterprise chatbots in 3-5 weeks. Customer support bots, sales bots, WhatsApp AI, voice agents. GPT-4, Claude, Llama. 70%+ ticket deflection guaranteed." />
        <meta name="keywords" content="AI chatbot development, chatbot development company, enterprise chatbot development, custom AI chatbot, AI chatbot developer, chatbot development services, WhatsApp AI chatbot, customer support chatbot, conversational AI development, chatbot development India" />
        <link rel="canonical" href="https://www.agilitytech.ai/ai-chatbot-development" />
        <meta property="og:title" content="AI Chatbot Development Company | Custom Chatbots Built in Weeks" />
        <meta property="og:description" content="Enterprise AI chatbots deployed in 3-5 weeks. 70%+ ticket deflection. GPT-4, Claude, WhatsApp, Voice. Agility — senior AI engineers, not consultants." />
        <meta property="og:url" content="https://www.agilitytech.ai/ai-chatbot-development" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "AI Chatbot Development",
            "description": "Custom enterprise AI chatbot development using GPT-4, Claude, and open-source LLMs. Deployed in 3-5 weeks.",
            "provider": { "@type": "Organization", "name": "Agility", "url": "https://www.agilitytech.ai" },
            "areaServed": "Worldwide",
            "serviceType": "AI Chatbot Development"
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-purple-900 via-black to-teal-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(30deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6),linear-gradient(150deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6)]" style={{ backgroundSize: '80px 140px' }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-full mb-8">
            <MessageSquare className="w-4 h-4 text-teal-400" />
            <span className="text-teal-300 text-sm font-medium">AI Chatbot Development</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            AI Chatbots That <span className="text-teal-400">Actually Work</span>
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl mx-auto">
            Custom AI chatbots deployed in 3–5 weeks. Customer support, sales, internal knowledge, voice —
            built on GPT-4o, Claude, and private LLMs with real integrations to your systems.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all">
              Build My Chatbot <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/case-studies" className="group inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold backdrop-blur-sm border border-white/20 transition-all">
              See Results <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[['3–5', 'Weeks to Launch'],['70%+', 'Avg Ticket Deflection'],['200+', 'AI Projects Shipped'],['24/7', 'Bot Uptime SLA']].map(([stat, label]) => (
              <div key={label} className="text-center">
                <div className="text-4xl font-bold text-teal-400 mb-2">{stat}</div>
                <div className="text-gray-400">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Every Type of <span className="text-teal-600">AI Chatbot</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From simple FAQ bots to complex multi-step agentic assistants — we build them all with production-grade reliability
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <div key={i} className="fade-in-up bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border-2 border-gray-200 hover:border-teal-500 transition-all">
                  <Icon className="w-12 h-12 text-teal-600 mb-6" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {service.features.map((f, j) => (
                      <span key={j} className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-medium">{f}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Chatbots We've <span className="text-teal-600">Shipped</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Real deployments with measurable business results</p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {caseStudies.map((study, i) => (
              <Link key={i} to={study.link} className="fade-in-up bg-gradient-to-br from-teal-50 to-white rounded-xl p-8 border border-teal-200 hover:border-teal-400 hover:shadow-lg transition-all group">
                <div className="text-sm font-semibold text-teal-600 mb-4">{study.industry}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Challenge</h3>
                <p className="text-gray-700 mb-4">{study.challenge}</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Solution</h3>
                <p className="text-gray-700 mb-4">{study.solution}</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Results</h3>
                <p className="text-gray-700 mb-6">{study.result}</p>
                <div className="flex items-center justify-between pt-6 border-t border-teal-200">
                  <div>
                    <div className="text-sm text-gray-500">Timeline</div>
                    <div className="font-semibold text-gray-900">{study.timeline} to production</div>
                  </div>
                  <div className="flex items-center gap-1 text-teal-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                    Read more <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              From Brief to <span className="text-teal-600">Live Bot in Weeks</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((p, i) => (
              <div key={i} className="fade-in-up text-center">
                <div className="w-20 h-20 bg-teal-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">{p.step}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{p.title}</h3>
                <p className="text-gray-600 mb-4">{p.description}</p>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-medium">
                  <Clock className="w-4 h-4" /> {p.duration}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Best-in-Class <span className="text-teal-400">Tech Stack</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">We choose the right tool for your use case — not the most fashionable one</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {techStack.map((t, i) => (
              <div key={i} className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-bold text-teal-400 mb-4">{t.category}</h3>
                <div className="flex flex-wrap gap-2">
                  {t.items.map((item, j) => (
                    <span key={j} className="px-3 py-1 bg-gray-700 text-gray-200 rounded-full text-sm">{item}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-teal-600 to-teal-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Build Your AI Chatbot?</h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Tell us your use case and we'll have a working prototype in front of you within a week.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 bg-white text-teal-700 hover:bg-teal-50 px-8 py-4 rounded-lg font-bold text-lg transition-all">
              Start Your Project <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/case-studies" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold text-lg transition-all">
              View All Case Studies
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AIChatbotDevelopment;
